import { Card, Group, Loader, Menu, Text, UnstyledButton } from "@mantine/core";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { checkSyncEnabled, getSyncStatus, initiateSync } from "@utils/api-communication/RestApiCli";
import { syncPoller } from "@utils/syncPolling";
import { getRole, isLoggedIn } from "@utils/CommonUtil";
import { useDispatch, useSelector } from "react-redux";

import LanguageChanger from "../../LanguageChanger";
import ColorSchemeToggle from "../../common/ColorSchemeToggle";
import GlobalDeviceSelector from "./GlobalDeviceSelector";
import { getPreferredTestModeUrl } from "@utils/testModeStorage";
import { Role } from "@model/role";
import { logOut } from "@actions/userActions";
import { resetTestState } from "@store/tests/testSlice";
import {
  selectUserForcePassChange,
  selectUserOnboarding,
} from "@utils/redux-utils/selectors/userSelectors";
import { hasOnboardingLicenses, ONBOARDING_PATH } from "@utils/onboarding/onboardingUtils";
import { useTranslation } from "react-i18next";
import { SCOPE_TYPE } from "@model";
import { useAuthContext } from "@service";
import { usePacsFeatureEnabled } from "@utils/dicom/pacsFeature";
import "./TestDashboardNav.scss";

export default function TestDashboardNav(_props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [role] = useState(getRole());
  // superadmin subsumes admin, mirroring role.utils.ts on the backend.
  const isAdmin = role === Role.Admin || role === Role.SuperAdmin;
  const [syncing, setSyncing] = useState(false);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [showNavigationLink, setShowNavigationLink] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const forcePasswordChange = useSelector(selectUserForcePassChange);
  const onboarding = useSelector(selectUserOnboarding);

  const { scopes } = useAuthContext();
  const pacsEnabled = usePacsFeatureEnabled();
  const newTestUrl = getPreferredTestModeUrl();

  useEffect(() => {
    if (isLoggedIn() && process.env.REACT_APP_OFFLINE === "true") {
      checkSyncEnabled({}, onSyncEnable, onSyncEnableError, null);
    }
  }, []);

  const cancelSyncPollRef = useRef<null | (() => void)>(null);

  useEffect(() => {
    return () => cancelSyncPollRef.current?.();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const loggedIn = isLoggedIn();
    setShowNavigationLink(loggedIn && !forcePasswordChange);
  }, [forcePasswordChange]);

  useEffect(() => {
    if (syncEnabled === false || !isLoggedIn()) {
      return;
    }
    getSyncStatus({}, onSyncStatus, null, null);
  }, [syncEnabled]);

  function onSyncStatus(response) {
    if (!response.data) {
      return;
    }
    if (response.data.inProgress) {
      followRunningSync();
    } else if (!response.data.hasEverSynced) {
      setSyncing(true);
      initiateSync({}, followRunningSync, onSyncRequestComplete, null);
    }
  }

  function followRunningSync() {
    setSyncing(true);
    cancelSyncPollRef.current?.();
    cancelSyncPollRef.current = syncPoller.follow(() => setSyncing(false));
  }

  function onSyncRequestComplete(_response) {
    setSyncing(false);
  }

  function onSyncEnable(response) {
    if (response.data === "true" || response.data === true) {
      setSyncEnabled(true);
    } else {
      setSyncEnabled(false);
    }
  }

  function onSyncEnableError(_response) {
    setSyncEnabled(false);
  }

  function handleLogout() {
    dispatch(logOut("/login"));
  }

  const navLinks = [
    {
      key: "new-test",
      href: newTestUrl,
      label: t("TestDashboard.new-test"),
      onClick: () => dispatch(resetTestState()),
    },
    { key: "list", href: "/list", label: t("testResults") },
    ...(scopes.includes(SCOPE_TYPE.testSession)
      ? [
          {
            key: "test-sessions",
            href: "/test-sessions",
            label: t("TestSessions.navTitle", "Test Sessions"),
          },
        ]
      : []),
    { key: "patients", href: "/patients", label: t("TestDashboard.patients") },
    { key: "progression", href: "/progression", label: t("TestDashboard.progression") },
  ];

  return (
    <nav
      className={`dash-nav print-hide${scrolled ? " scrolled" : ""}${mobileOpen ? " mobile-open" : ""}`}
    >
      <div className="dash-nav-inner">
        <a href="/" className="dash-nav-logo">
          <img src="/img/little-logo.png" className="logo-mark" alt={"oculera"} />
          <img src="/img/logo.png" className="logo-full" alt={"oculera"} />
        </a>
        {showNavigationLink ? <GlobalDeviceSelector /> : null}
        {process.env.REACT_APP_OFFLINE === "true" ? (
          syncing ? (
            <Card className="dash-offline-card" shadow={"sm"} radius={"lg"} p={"xs"}>
              <Group gap={"sm"} align="center">
                <Text c="dimmed">{t("Synchronizing")}</Text>
                <Loader size="sm" color="blue" />
              </Group>
            </Card>
          ) : (
            <Card className="dash-offline-card" shadow={"sm"} radius={"lg"} p={"xs"} withBorder>
              <Text fw={600} c="dimmed">
                {t("OfflineMode")}
              </Text>
            </Card>
          )
        ) : null}
        {showNavigationLink ? (
          <>
            <ul id="dash-nav-links" className={`dash-nav-links${mobileOpen ? " open" : ""}`}>
              {navLinks.map((item) => (
                <li key={item.key}>
                  <a
                    href={item.href}
                    onClick={() => {
                      setMobileOpen(false);
                      item.onClick?.();
                    }}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
            <div className="dash-nav-right">
              <div className="dash-nav-utils">
                <ColorSchemeToggle />
                <LanguageChanger />
              </div>
              <div className="dash-settings">
                <Menu
                  position="bottom-end"
                  withinPortal={false}
                  classNames={{ dropdown: "dash-settings-dropdown", item: "dash-settings-item" }}
                >
                  <Menu.Target>
                    <UnstyledButton className="dash-settings-toggle">
                      {t("TestDashboard.setting")}
                    </UnstyledButton>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item component={Link} to="/device">
                      {t("TestDashboard.device")}
                    </Menu.Item>
                    <Menu.Item component={Link} to="/system">
                      {t("TestDashboard.system", "System")}
                    </Menu.Item>
                    <Menu.Item component={Link} to="/user-settings">
                      {t("TestDashboard.user")}
                    </Menu.Item>
                    <Menu.Item component={Link} to="/versions">
                      {t("TestDashboard.versions")}
                    </Menu.Item>
                    <Menu.Item component={Link} to="/tutorial">
                      Tutorial
                    </Menu.Item>
                    <Menu.Item component={Link} to="/help">
                      {t("TestDashboard.help", "Help")}
                    </Menu.Item>
                    <Menu.Item component={Link} to="/organization-settings">
                      {t("OrganizationSettings.settings")}
                    </Menu.Item>
                    {hasOnboardingLicenses(onboarding) && (
                      <Menu.Item
                        component={Link}
                        to={ONBOARDING_PATH}
                        data-testid="nav-onboarding-link"
                      >
                        {t("TestDashboard.onboarding", "Onboarding")}
                      </Menu.Item>
                    )}
                    {pacsEnabled && (
                      <Menu.Item component={Link} to="/dicom-settings">
                        {t("TestDashboard.dicomSettings", "DICOM / PACS")}
                      </Menu.Item>
                    )}
                    {scopes.includes(SCOPE_TYPE.dataExport) && (
                      <Menu.Item component={Link} to="/data-export">
                        {"Data Export"}
                      </Menu.Item>
                    )}
                    {scopes.includes(SCOPE_TYPE.translation) && (
                      <Menu.Item component={Link} to="/translations">
                        {t("TestDashboard.translations", "Translations")}
                      </Menu.Item>
                    )}
                    {scopes.includes(SCOPE_TYPE.raTextEditor) && (
                      <Menu.Item component={Link} to="/ra-text-editor">
                        {t("TestDashboard.raTextEditor", "RA Text Editor")}
                      </Menu.Item>
                    )}
                    {role === Role.Manager && (
                      <Menu.Item component={Link} to="/manager-dashboard">
                        {"Manager Panel"}
                      </Menu.Item>
                    )}
                    {(role === Role.Distributor || isAdmin) && (
                      <Menu.Item component={Link} to="/distributor-dashboard">
                        {"Distributor Panel"}
                      </Menu.Item>
                    )}
                    {scopes.includes(SCOPE_TYPE.installer) && (
                      <Menu.Item component={Link} to="/installer">
                        {t("TestDashboard.installer", "Installer")}
                      </Menu.Item>
                    )}
                    {(isAdmin || role === Role.Distributor) && (
                      <Menu.Item component={Link} to="/analytics-dashboard">
                        {t("TestDashboard.analytics")}
                      </Menu.Item>
                    )}
                    {isAdmin && (
                      <Menu.Item component={Link} to="/admin-dashboard">
                        {"Admin Panel"}
                      </Menu.Item>
                    )}
                    <Menu.Item onClick={handleLogout}>{t("TestDashboard.logout")}</Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </div>
            </div>
            <button
              type="button"
              className="dash-nav-hamburger"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={t("TestDashboard.toggleNavigation", "Toggle navigation")}
              aria-expanded={mobileOpen}
              aria-controls="dash-nav-links"
            >
              <span />
              <span />
              <span />
            </button>
          </>
        ) : null}
      </div>
    </nav>
  );
}
